import React, { useCallback, useState } from 'react';
import { extractFilesFromDropEvent } from '../../utils/dropFileExtractor';
import './FileDropZone.css';

/**
 * The one drop zone every upload surface in HMS uses.
 *
 * Why this exists: for a long time each upload modal hand-rolled its own
 * `onDragOver`/`onDrop` pair next to its own "Drop files here" wording. The
 * wording and the behaviour are separate things in that arrangement, so they
 * drifted — the delivery document upload modal shipped the words with no
 * handlers behind them at all, and dropping a file there did nothing while
 * looking exactly like a working drop zone. Routing every surface through one
 * component makes that impossible: you cannot render the invitation without
 * also rendering the behaviour.
 *
 * Two rules this component enforces that hand-rolled zones kept getting wrong:
 *
 *   1. `onFiles` is REQUIRED and is refused loudly if absent. A drop zone with
 *      no consumer is the exact defect this component was written to kill, so
 *      it must not be expressible as a silent no-op.
 *   2. Every drop goes through `extractFilesFromDropEvent`, so a drag straight
 *      out of Outlook or Gmail produces an explanation instead of silence.
 *
 * `children` may be a render function receiving `{ dragActive }` for surfaces
 * that style their own "ready to receive" state inline.
 *
 * `data-file-drop-zone` marks the element for the app-wide stray-drop guard
 * (see utils/strayDropGuard.js), which swallows drops everywhere else so a
 * near-miss cannot make the browser navigate away and bin unsaved work.
 */
const FileDropZone = ({
    onFiles,
    onError,
    multiple = false,
    disabled = false,
    className = '',
    activeClassName = '',
    onClick,
    children,
    as: Tag = 'div',
    ...rest
}) => {
    const [dragActive, setDragActive] = useState(false);

    if (typeof onFiles !== 'function') {
        // Refuse loudly rather than rendering an inviting box that eats files.
        throw new Error(
            'FileDropZone requires an onFiles(files) handler — a drop zone that ' +
            'cannot hand the file anywhere is the bug this component exists to prevent.'
        );
    }

    const stop = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = useCallback((e) => {
        stop(e);
        if (disabled) return;
        setDragActive(true);
    }, [disabled]);

    const handleDragOver = useCallback((e) => {
        stop(e);
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
        }
        if (disabled) return;
        setDragActive(true);
    }, [disabled]);

    const handleDragLeave = useCallback((e) => {
        stop(e);
        setDragActive(false);
    }, []);

    const handleDrop = useCallback((e) => {
        stop(e);
        setDragActive(false);
        if (disabled) return;

        const { files, error } = extractFilesFromDropEvent(e);

        if (error || files.length === 0) {
            if (typeof onError === 'function') {
                onError(error || 'No file was detected in the drop.');
            }
            return;
        }

        if (!multiple && files.length > 1 && typeof onError === 'function') {
            onError(
                `Only one file can be uploaded here — using "${files[0].name}" and ` +
                'ignoring the rest.'
            );
        }

        onFiles(multiple ? files : files.slice(0, 1));
    }, [disabled, multiple, onFiles, onError]);

    const activeClass = dragActive
        ? (activeClassName || 'hms-file-drop-zone--active')
        : '';

    return (
        <Tag
            {...rest}
            data-file-drop-zone="true"
            data-drag-active={dragActive ? 'true' : 'false'}
            className={`hms-file-drop-zone ${className} ${activeClass}`.replace(/\s+/g, ' ').trim()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={disabled ? undefined : onClick}
        >
            {typeof children === 'function' ? children({ dragActive }) : children}
        </Tag>
    );
};

export default FileDropZone;
